<template>
  <div class="strategic-plans-show w-100">
    <div v-if="!strategic_plan || treeLoading" class="show-main-content">
      <div class="skeleton-header" />
      <div class="skeleton-counters">
        <div class="skeleton-counter" v-for="i in 4" :key="i" />
      </div>
      <div class="skeleton-tree">
        <div class="skeleton-node" v-for="i in 3" :key="i" />
      </div>
    </div>

    <template v-else>
      <div class="show-main-content">
        <!-- 2. Plan Header (dark card) -->
        <div class="plan-header-card">
          <div class="d-flex plan-header-top">
            <!-- Left: Icon + Title -->
            <div class="d-flex align-items-start plan-header-left">
              <div class="plan-icon-wrap mr-3">
                <font-awesome-icon :icon="planIcon" class="plan-icon" />
              </div>
              <div class="plan-header-title-area">
                <h1 class="plan-title">
                  {{ strategic_plan.name || 'Plano Plurianual' }}
                </h1>
              </div>
            </div>

            <!-- Center: Metadata grid (desktop) -->
            <div class="plan-meta-grid plan-meta-grid-desktop">
              <div class="plan-meta-item">
                <span class="plan-meta-label">Responsável</span>
                <p class="plan-meta-value">
                  <font-awesome-icon :icon="['fas', 'user']" class="plan-meta-icon" />
                  {{ responsibleText }}
                </p>
              </div>
              <div class="plan-meta-item">
                <span class="plan-meta-label">Vigência</span>
                <p class="plan-meta-value">
                  <font-awesome-icon :icon="['fas', 'calendar']" class="plan-meta-icon" />
                  {{ vigenciaText }}
                </p>
              </div>
              <div v-if="hasInstrumentoLegal" class="plan-meta-item">
                <span class="plan-meta-label">Instrumento Legal</span>
                <p class="plan-meta-value">
                  <font-awesome-icon :icon="['fas', 'file-alt']" class="plan-meta-icon" />
                  {{ instrumentoText }}
                </p>
              </div>
              <div class="plan-meta-item">
                <span class="plan-meta-label">Última Atualização</span>
                <p class="plan-meta-value">
                  <font-awesome-icon :icon="['fas', 'clock']" class="plan-meta-icon" />
                  {{ lastUpdateText }}
                </p>
              </div>
            </div>

          </div>

          <!-- Mobile metadata -->
          <div class="plan-meta-grid-mobile">
            <div class="plan-meta-item">
              <span class="plan-meta-label">Responsável</span>
              <p class="plan-meta-value">
                <font-awesome-icon :icon="['fas', 'user']" class="plan-meta-icon" />
                {{ responsibleText }}
              </p>
            </div>
            <div class="plan-meta-item">
              <span class="plan-meta-label">Vigência</span>
              <p class="plan-meta-value">
                <font-awesome-icon :icon="['fas', 'calendar']" class="plan-meta-icon" />
                {{ vigenciaText }}
              </p>
            </div>
            <div v-if="hasInstrumentoLegal" class="plan-meta-item">
              <span class="plan-meta-label">Instrumento Legal</span>
              <p class="plan-meta-value">
                <font-awesome-icon :icon="['fas', 'file-alt']" class="plan-meta-icon" />
                {{ instrumentoText }}
              </p>
            </div>
            <div class="plan-meta-item">
              <span class="plan-meta-label">Última Atualização</span>
              <p class="plan-meta-value">
                <font-awesome-icon :icon="['fas', 'clock']" class="plan-meta-icon" />
                {{ lastUpdateText }}
              </p>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="plan-header-actions">
            <button
              v-if="$can('strategic_plan:edit')"
              type="button"
              class="btn-plan-edit"
              @click="openEditStrategicPlan"
            >
              <font-awesome-icon :icon="['fas', 'pencil-alt']" class="mr-1" />
              Editar
            </button>
            <button type="button" class="btn-plan-delete" @click="deletePlan">
              <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
              Excluir
            </button>
          </div>
        </div>

        <!-- Tab navigation -->
        <PlanStructureTabs
          active-tab="estrutura"
          :structure-detail="getStructureDetailText"
          :action-badges="{}"
          :strategic-plan-id="strategic_plan.id"
          scroll-target-selector="#tree-panel-scroll"
        />

        <!-- 3-5. Tree panel: toolbar + tree -->
        <div id="tree-panel-scroll" class="tree-panel">
          <!-- Toolbar: search + Expand All -->
          <div class="tree-toolbar mb-3">
            <div class="search-wrap search-wrap-inline">
              <span class="search-icon">
                <font-awesome-icon :icon="['fas', 'search']" />
              </span>
              <input
                v-model="searchText"
                type="text"
                class="search-input"
                placeholder="Buscar áreas, projetos, ações..."
              />
            </div>
            <div class="tree-toolbar-actions">
              <button
                v-if="$can('strategic_plan:edit')"
                type="button"
                class="btn-add-level btn-expand-toggle"
                @click="handleAddFirstLevel"
              >
                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                Adicionar {{ firstLevelLabel }}
              </button>
              <button
                type="button"
                class="btn-expand-toggle"
                @click="toggleExpandAll"
              >
                {{ expandAll ? 'Recolher todos' : 'Expandir todos' }}
                <font-awesome-icon :icon="['fas', expandAll ? 'chevron-up' : 'chevron-down']" class="ml-1" />
              </button>
            </div>
          </div>

          <!-- Hierarchy Tree -->
          <div v-if="filteredStructure.length > 0" class="tree-container">
            <TreeNodeCard
              v-for="item in filteredStructure"
              :key="item.id"
              :item="item"
              :depth="0"
              :strategic-plan-id="strategic_plan.id"
              :expand-all="expandAll"
              :search-text="searchText"
              :plan-levels="planLevels"
              @add-child="handleAddChild"
              @edit-node="handleEditNode"
              @delete-node="handleDeleteNode"
              @add-action-plan="handleAddActionPlan"
              @action-plan-created="reloadTree"
              @action-item-created="reloadTree"
              @reload="reloadTree"
            />
          </div>
          <div v-else class="empty-search-result">
            Nenhum resultado encontrado para "{{ searchText }}"
          </div>
        </div>
      </div>

      <ModalEditStrategicPlan ref="modalEditPlan" />
      <ModalAddLevel ref="modalAddLevel" :strategic-plan-id="strategic_plan.id" @saved="reloadTree" />
      <ModalEditLevel ref="modalEditLevel" :strategic-plan-id="strategic_plan.id" @saved="reloadTree" />
      <ModalAddActionPlan ref="modalAddActionPlan" @saved="onActionPlanSaved" />
    </template>
  </div>
</template>

<script>
import TreeNodeCard from '../components/ShowTree/TreeNodeCard.vue';
import ModalEditStrategicPlan from '../components/ShowTree/ModalEditStrategicPlan.vue';
import ModalAddLevel from '../components/ShowTree/ModalAddLevel.vue';
import ModalEditLevel from '../components/ShowTree/ModalEditLevel.vue';
import ModalAddActionPlan from '../components/ShowTree/ModalAddActionPlan.vue';
import PlanStructureTabs from '../components/PlanStructureTabs.vue';
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'StrategicPlansShow',
  components: {
    TreeNodeCard,
    ModalEditStrategicPlan,
    ModalAddLevel,
    ModalEditLevel,
    ModalAddActionPlan,
    PlanStructureTabs,
  },
  data() {
    return {
      searchText: '',
      expandAll: false,
      treeLoading: false,
    };
  },
  computed: {
    ...mapState('strategic_plans', [
      'strategic_plan',
      'tree_plan_structure',
    ]),
    ...mapGetters('strategic_plans', [
      'getTreePlanStructureLength',
      'getStructureDetailText',
    ]),
    planIcon() {
      const icon = (this.strategic_plan?.icon || '').trim();
      if (!icon) return ['fas', 'graduation-cap'];

      const normalized = icon.replace(/\s+/g, ' ').trim().split(' ');
      const candidate = normalized.find((p) => p.startsWith('fa-'));
      const styleToken = normalized.find((p) =>
        ['fas', 'far', 'fal', 'fab', 'fad', 'fat', 'fa-solid', 'fa-regular', 'fa-light', 'fa-brands', 'fa-duotone', 'fa-thin'].includes(p)
      );

      const iconName = (candidate || '').replace(/^fa-/, '');
      if (!iconName) return ['fas', 'graduation-cap'];

      if (styleToken && styleToken.startsWith('fa-')) {
        const styleMap = {
          'fa-solid': 'fas',
          'fa-regular': 'far',
          'fa-light': 'fal',
          'fa-brands': 'fab',
          'fa-duotone': 'fad',
          'fa-thin': 'fat',
        };
        return [styleMap[styleToken] || 'fas', iconName];
      }

      if (styleToken && ['fas', 'far', 'fal', 'fab', 'fad', 'fat'].includes(styleToken)) {
        return [styleToken, iconName];
      }

      return ['fas', iconName];
    },
    lastUpdateText() {
      const u = this.strategic_plan?.updated_at;
      return u ? (this.$options.filters?.format_date?.(u) || u) : '–';
    },
    responsibleText() {
      const r = this.strategic_plan?.responsible;
      const o = this.strategic_plan?.organizationalStructure;
      if (r && o) return `${r.name} / ${o.name}`;
      if (r) return r.name;
      return 'Não definido';
    },
    vigenciaText() {
      const start = this.strategic_plan?.start_term;
      const end = this.strategic_plan?.end_term;
      if (!start && !end) return '–';
      return [start, end].filter(Boolean).map((d) => this.$options.filters?.format_date?.(d) || d).join(' a ');
    },
    hasInstrumentoLegal() {
      const sp = this.strategic_plan;
      if (!sp) return false;
      const inst = String(sp.legal_instrument || '').trim();
      const link = String(sp.legal_instrument_link || '').trim();
      return !!(inst || sp.legal_instrument_date || link);
    },
    instrumentoText() {
      const inst = String(this.strategic_plan?.legal_instrument || '').trim();
      const date = this.strategic_plan?.legal_instrument_date;
      const y = date ? (this.$options.filters?.format_date?.(date, 'YYYY') || date) : '';
      const text = [inst, y].filter(Boolean).join(' / ');
      if (text) return text;
      if (String(this.strategic_plan?.legal_instrument_link || '').trim()) return 'Ver instrumento';
      return '–';
    },
    planLevels() {
      return this.strategic_plan?.config?.plan_structure?.levels || [];
    },
    firstLevelLabel() {
      const levels = this.planLevels;
      return levels[0] || 'Nível 1';
    },
    totalAcoes() {
      const countFromTree = (nodes) => {
        let count = 0;
        (nodes || []).forEach((n) => {
          count += n.total_acoes || 0;
        });
        return count;
      };
      return countFromTree(this.tree_plan_structure || []);
    },
    filteredStructure() {
      const text = (this.searchText || '').toLowerCase().trim();
      if (!text) return this.tree_plan_structure || [];
      const structure = this.tree_plan_structure || [];
      return this.filterTreeBySearch(structure, text);
    },
  },
  provide() {
    return {
      reloadTree: () => this.reloadTree(),
    };
  },
  methods: {
    ...mapActions('strategic_plans', [
      'ActionShow',
      'ActionLoadPlanStructure',
    ]),
    goToAcoes() {
      this.$router.push({
        name: 'acoes_list',
        query: { strategicPlanId: this.strategic_plan.id },
      });
    },
    /** Verifica se o próprio nó corresponde à busca (apenas campos diretos, sem children/acoes). */
    nodeSelfMatchesSearch(node, text) {
      if (!node) return false;
      const { children, acoes, ...rest } = node;
      const str = JSON.stringify(rest).toLowerCase();
      return str.includes(text);
    },
    /** Verifica se uma ação (plano de ação) corresponde à busca. */
    acaoMatchesSearch(acao, text) {
      if (!acao) return false;
      const str = JSON.stringify(acao).toLowerCase();
      return str.includes(text);
    },
    /**
     * Filtra a árvore recursivamente: mantém apenas os ramos que levam a correspondências.
     * Se um Nível 3 ou ação corresponde, retorna só o caminho: Nível 1 → Nível 2 → Nível 3/ação correspondente.
     */
    filterTreeBySearch(nodes, text) {
      if (!nodes || nodes.length === 0) return [];
      return nodes
        .map((node) => this.filterNodeBySearch(node, text))
        .filter(Boolean);
    },
    filterNodeBySearch(node, text) {
      const nodeSelfMatches = this.nodeSelfMatchesSearch(node, text);
      const filteredChildren = this.filterTreeBySearch(node.children || [], text);
      const filteredAcoes = (node.acoes || []).filter((a) => this.acaoMatchesSearch(a, text));

      const hasMatchingDescendant = filteredChildren.length > 0 || filteredAcoes.length > 0;
      if (!nodeSelfMatches && !hasMatchingDescendant) return null;

      const result = { ...node };
      if (nodeSelfMatches) {
        result.children = node.children || [];
        result.acoes = node.acoes || [];
      } else {
        result.children = filteredChildren;
        result.acoes = filteredAcoes;
      }
      return result;
    },
    openEditStrategicPlan() {
      this.$refs.modalEditPlan.show(this.strategic_plan.id);
    },
    toggleExpandAll() {
      this.expandAll = !this.expandAll;
    },
    handleAddFirstLevel() {
      this.$refs.modalAddLevel.show(null, 0);
    },
    handleAddChild(parentNode) {
      const parentDepth = parentNode.depth ?? 0;
      const newDepth = parentDepth + 1;
      const maxDepth = this.planLevels?.length ?? 1;
      if (newDepth >= maxDepth) {
        this.$notifications.addNotification({
          message: `Limite máximo de ${maxDepth} níveis atingido.`,
          type: 'warning',
        });
        return;
      }
      this.$refs.modalAddLevel.show(parentNode.id, newDepth);
    },
    handleEditNode(node) {
      const depth = node.depth ?? 0;
      this.$refs.modalEditLevel.show(node, depth);
    },
    async handleDeleteNode(node) {
      const value = await this.$bvModal.msgBoxConfirm('Essa operação não poderá ser desfeita.', {
        title: 'Deseja realmente excluir?',
        okVariant: 'danger',
        okTitle: 'SIM',
        size: 'md',
        cancelVariant: 'outline-primary',
        cancelTitle: 'NÃO',
        centered: true,
      });
      if (!value) return;
      try {
        await this.$store.dispatch('strategic_plans/ActionDeletePlanStructure', node.id);
        await this.reloadTree();
      } catch (e) {
        this.$notifications.addNotification({
          message: 'Erro ao excluir nível.',
          type: 'danger',
        });
      }
    },
    handleAddActionPlan(node) {
      this.$refs.modalAddActionPlan.show(node.id);
    },
    async onActionPlanSaved() {
      await this.reloadTree();
    },
    async loadData(id) {
      this.treeLoading = true;
      try {
        await this.ActionShow({ id });
        await this.ActionLoadPlanStructure();
      } finally {
        this.treeLoading = false;
      }
    },
    async reloadTree() {
      await this.ActionLoadPlanStructure();
    },
    deletePlan() {
      this.$bvModal
        .msgBoxConfirm('Essa operação não poderá ser desfeita.', {
          title: 'Deseja realmente excluir?',
          okVariant: 'danger',
          okTitle: 'SIM',
          size: 'md',
          cancelVariant: 'outline-primary',
          cancelTitle: 'NÃO',
          centered: true,
        })
        .then(async (value) => {
          if (value) {
            await this.$store.dispatch('strategic_plans/ActionDelete', this.strategic_plan.id);
            this.$router.push({ name: 'strategic_plans_list' });
          }
        });
    },
  },
  async created() {
    const id = this.$route.params.id;
    await this.loadData(id);
  },
};
</script>

<style scoped>
.search-wrap {
  position: relative;
  flex: 1 1 auto;
  max-width: 20rem;
}
.search-wrap-inline {
  max-width: 22rem;
}
.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  font-size: 0.875rem;
}
.search-input {
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  font-size: 0.875rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, border-color 0.2s;
}
.search-input::placeholder {
  color: #9ca3af;
}
.search-input:focus {
  outline: none;
  border-color: rgba(var(--primary-rgb), 0.4);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.15);
}

/* ===== Main content area ===== */
.show-main-content {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.5rem 1rem;
}

/* ===== Plan header card ===== */
.plan-header-card {
  background: #343a40;
  border-radius: 0.75rem;
  color: #fff;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15);
  margin-bottom: 0.5rem;
}
.plan-header-top {
  gap: 1.5rem;
  align-items: flex-start;
}
.plan-header-left {
  flex-shrink: 1;
  min-width: 0;
}
.plan-icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.plan-icon {
  font-size: 2rem;
  color: #fff;
}
.plan-header-title-area {
  min-width: 0;
  padding-top: 0.125rem;
}
.plan-title {
  font-size: 1.25rem; /* text-xl */
  font-weight: 700; /* font-bold */
  line-height: 1.2; /* leading-tight */
  letter-spacing: -0.02em; /* tracking-tight */
  margin: 0;
  color: #fff;
}
@media (min-width: 576px) {
  .plan-title {
    font-size: 1.5rem; /* text-2xl */
  }
}

/* Metadata grid (center) */
.plan-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 2.5rem;
  flex: 1;
}
.plan-meta-grid-desktop {
  display: none;
}
.plan-meta-grid-mobile {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.plan-meta-label {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  opacity: 0.7;
  display: block;
}
.plan-meta-value {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0.125rem 0 0 0;
  font-size: 0.875rem; /* text-sm */
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
}
.plan-meta-icon {
  font-size: 0.875rem;
  opacity: 0.7;
}


/* Action buttons */
.plan-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0;
  border-top: none;
}
.btn-plan-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.5rem;
  background: #0d6efd;
  border: 1px solid rgba(13, 110, 253, 0.9);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.btn-plan-edit:hover {
  background: rgba(13, 110, 253, 0.9);
  color: #fff;
  text-decoration: none;
  box-shadow: 0 6px 14px rgba(13, 110, 253, 0.25);
}
.btn-plan-delete {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.5rem;
  background: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.9);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.btn-plan-delete:hover {
  background: rgba(220, 53, 69, 0.9);
  box-shadow: 0 6px 14px rgba(220, 53, 69, 0.25);
}

/* ===== Counter cards ===== */
.tree-panel {
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: var(--shadow-card);
  padding: 1rem;
}

.counters-row {
  margin-top: 0.25rem;
}
.counter-card {
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.15s;
}
.counter-card-clickable {
  cursor: pointer;
}
.counter-card-clickable:hover {
  background: #f9fafb;
  border-color: rgba(var(--primary-rgb), 0.3);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.06);
}
.counter-label {
  font-size: 0.75rem;
  font-weight: 600; /* font-semibold */
  color: var(--muted-foreground, #6b7280);
  letter-spacing: 0.04em; /* tracking-wide */
  display: block;
}
.counter-value {
  font-size: 1.875rem;
  font-weight: 500;
  color: var(--foreground, #1f2937);
  margin-top: 0.375rem;
  font-family: var(--font-heading, 'Inter', 'Roboto', sans-serif);
}
.counter-link {
  font-size: 0.6875rem; /* text-[11px] */
  font-weight: 600; /* font-semibold */
  color: var(--primary);
  margin-top: 0.375rem;
  display: inline-block;
}

/* ===== Expand/Collapse toggle ===== */
.btn-expand-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  background: var(--primary);
  border: 1px solid rgba(var(--primary-rgb), 0.85);
  border-radius: 0.5rem;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.btn-expand-toggle:hover {
  color: #fff;
  background: rgba(var(--primary-rgb), 0.9);
  border-color: rgba(var(--primary-rgb), 0.95);
  box-shadow: 0 4px 10px rgba(var(--primary-rgb), 0.2);
}

/* ===== Empty state ===== */
.empty-search-result {
  text-align: center;
  padding: 3rem 1rem;
  color: #9ca3af;
}

.tree-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.tree-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ===== Responsive ===== */
@media (max-width: 575.98px) {
  .search-wrap {
    max-width: none;
  }
  .tree-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .btn-expand-toggle {
    justify-content: center;
  }
  .plan-header-card {
    padding: 1.25rem 1rem;
  }
  .plan-header-top {
    flex-wrap: wrap;
  }
}
@media (min-width: 768px) {
  .plan-meta-grid-desktop {
    display: grid;
  }
  .plan-meta-grid-mobile {
    display: none;
  }
}

/* ===== Skeleton Loading ===== */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.skeleton-header,
.skeleton-counter,
.skeleton-node {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.75rem;
}
.skeleton-header {
  height: 8rem;
  margin-bottom: 1rem;
}
.skeleton-counters {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}
.skeleton-counter {
  height: 5rem;
}
.skeleton-tree {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.skeleton-node {
  height: 4.5rem;
}
</style>
